import React, { useContext, createContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from 'react-apollo';
import { BasePage, Helmet, StaticContext, SurveyTheme, FourOhFourErrorPage } from '@sm/webassets';
import { Box } from '@wds/box';
import { t } from '@sm/intl';
import { Header, EnterpriseThankYouBody, EnterpriseThankYouFooter, EnterpriseThankYouLayout } from './components';
import { getDefaultEndPageSettings } from './EnterpriseThankYou.graphql';
import COPY from './copy';
import useStyles from './useStyles';

export const PageVariants = {
  standard: Symbol('standard'),
  premiumStandard: Symbol('premium_standard'),
  premiumCustom: Symbol('premium_custom'),
};
export const EnterpriseThankYouContext = createContext<{ pageVariant?: symbol }>({});

const determinePageVariant = (isStandard: boolean, isPremiumCustomizable: boolean): symbol => {
  if (isStandard) {
    return PageVariants.standard;
  }
  if (isPremiumCustomizable) {
    return PageVariants.premiumCustom;
  }
  return PageVariants.premiumStandard;
};

function EnterpriseThankYou(): React.ReactElement | null {
  const { pageOptions } = useContext(StaticContext);
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const smParam = searchParams.get('sm');
  const { surveyId, hasStandard } = pageOptions;

  // Pass the encrypted parameter back to lo-graph use case for basic authentication
  const thankYouHeaders = { 'X-SM-PARAM': smParam };
  const queryOptions = {
    variables: { smParam },
    context: {
      targetPath: '/lo-graphql/enterpriseThankYou',
      headers: thankYouHeaders,
    },
  };
  const { loading, error, data: endPageSettings } = useQuery(getDefaultEndPageSettings, queryOptions);
  const { outline, contentOutline } = useStyles();

  if (loading) {
    return null;
  }

  if (error || endPageSettings === null) {
    return <FourOhFourErrorPage />;
  }

  if (endPageSettings) {
    const customEndPageIsEnabled = endPageSettings?.defaultEndPageSettings?.isEnabled; // Used to distinguish between the "custom" and "standard" premium pages, which is different from standard/premium vars taken from smParam
    const headerLogoUrl = endPageSettings?.defaultEndPageSettings?.logo?.url;
    const headerText = endPageSettings?.defaultEndPageSettings?.thankYouText;
    const bodyImageUrl = endPageSettings?.defaultEndPageSettings?.image?.url;
    const bodyText = endPageSettings?.defaultEndPageSettings?.bodyText;
    const bodyImageRedirectUrl = endPageSettings?.defaultEndPageSettings?.imageRedirectUrl;
    const pageVariant = determinePageVariant(hasStandard, customEndPageIsEnabled);

    return (
      <BasePage
        actionFlow="EnterpriseThankYou"
        pageId="EnterpriseThankYou"
        legacyWeb="respweb"
        includeHeader={false}
        includeFooter={false}
      >
        <Helmet>
          <base target="_top" href="/" />
          <title>{t(COPY.THANK_YOU)}</title>
          <meta name="description" content={t(COPY.THANK_YOU)} />
        </Helmet>
        <SurveyTheme surveyId={surveyId} targetEndPoint="enterpriseThankYou" headers={thankYouHeaders}>
          <EnterpriseThankYouContext.Provider value={{ pageVariant }}>
            <EnterpriseThankYouLayout>
              <Box className={outline} flexJustify="center" py={9} data-testid="EnterpriseThankYou__OutlineContainer">
                <Box className={contentOutline}>
                  <Header headerLogoUrl={headerLogoUrl} headerText={headerText} />
                  <EnterpriseThankYouBody
                    bodyImageUrl={bodyImageUrl}
                    bodyText={bodyText}
                    bodyImageRedirectUrl={bodyImageRedirectUrl}
                  />
                </Box>
                <EnterpriseThankYouFooter />
              </Box>
            </EnterpriseThankYouLayout>
          </EnterpriseThankYouContext.Provider>
        </SurveyTheme>
      </BasePage>
    );
  }
  return null;
}

export default EnterpriseThankYou;
